import React, { useMemo } from "react";

import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { IconButton, Stack, Typography } from "@mui/material";
import { Cloud, VisibilityLock, WorkspaceFolderCloseIcon } from "assets/material-custom-icons";
import { DealRoleGuard, SharedDealGuard, StatusComponentGuard } from "components/guards";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useIsViewerPermission } from "hooks";
import { useOpportunityIdSelector } from "store";
import { IFolder } from "store/types";

import { RoleName } from "../../../constants";
import { useDealWorkspace } from "../context/DealWorkspace-context";
import { useFolderHook } from "../Hooks/use-folder-hook";
import { useFolderDragDrop } from "../Hooks/use-workspace-dnd";
import { FolderActions } from "../Menus/FolderActions";

export const ROW_HEIGHT = 36;

interface Props {
  folder: IFolder;
  depth: number;
  folderList: IFolder[];
}

const INDENT_PX = 25;
const chevronColor = "#0A3142";
const iconColor = "#0A3142";
const labelColor = "#20293C";

export const Folder: React.FC<Props> = React.memo(({ folder, depth, folderList }) => {
  const opportunityId = useOpportunityIdSelector();
  const { isFolderOpen, onFolderArrowClick, currentFolder, onFolderClick, getChildFolders, refetchFolderMetadata } =
    useDealWorkspace();

  const isAnalystDealsViewerPermission = useIsViewerPermission("deals");

  const childFolders = getChildFolders(folder.id, folderList);
  const isOpen = isFolderOpen(folder.id);
  const hasChildren = Boolean(childFolders.length);
  const isSelected = currentFolder?.id === folder.id;
  const isCloudFolder = folder.kind === "dataroom";

  const folderIcon = useMemo(() => {
    if (folder.kind === "dataroom") return <Cloud sx={{ fill: iconColor, fontSize: "20px" }} />;
    return <WorkspaceFolderCloseIcon sx={{ fill: iconColor, fontSize: "20px" }} />;
  }, [folder.kind, folder.parent_id]);

  const showHint = useMemo(() => {
    if (folder.kind === "dataroom" && !folder.parent_id)
      return "Replica of the connected data room folder structure. Files are synced directly from the external data room and cannot be edited or added manually.";
    if (folder.title === "Altius classification (Old)" && !folder.parent_id)
      return "All altius classification were moved to this folder";
    return "";
  }, [folder.title]);

  const isEmptyDataRoomRootFolder = useMemo(() => {
    return folder.kind === "dataroom" && !folder.parent_id && !hasChildren;
  }, [folder.kind, folder.parent_id, hasChildren]);

  const { onDropFiles } = useFolderHook({ folder, refetchHandler: refetchFolderMetadata });
  const {
    isDragOver,
    // onDragStart, onDragEnd,
    onDragOver,
    onDragLeave,
    onDrop
  } = useFolderDragDrop({
    folder,
    onDropFiles
  });

  const onFolderArrowClickHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasChildren) {
      onFolderArrowClick(folder.id);
    }
  };

  const handleTitleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFolderClick({ folderId: folder.id, isCloudFolder });
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const folderPadding = useMemo(() => {
    if (isEmptyDataRoomRootFolder) return "35px";
    if (depth) return `${depth * INDENT_PX + 10}px`;
    return "5px";
  }, [depth]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="5px"
      draggable={!isCloudFolder}
      onContextMenu={handleContextMenu}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      sx={{
        width: "100%",
        height: ROW_HEIGHT,
        padding: "0 5px",
        borderRadius: "20px",
        paddingLeft: folderPadding,
        backgroundColor: isDragOver ? "#C8DCF0" : isSelected ? "#DBE8F5" : "transparent",
        outline: isDragOver ? "2px dashed #5B9BD5" : "none",
        outlineOffset: "-2px",
        "&:hover": {
          backgroundColor: isDragOver ? "#C8DCF0" : "#ECF1F8"
        },
        "& .folder-menu-trigger": {
          opacity: 0
        },
        "&:hover .folder-menu-trigger": {
          opacity: 0.7
        },
        "& .folder-menu-trigger:hover": {
          opacity: 1
        }
      }}
    >
      {hasChildren ? (
        <IconButton onClick={onFolderArrowClickHandler} sx={{ cursor: "pointer" }}>
          <ExpandMoreOutlinedIcon
            sx={{
              fill: chevronColor,
              fontSize: 20,
              transform: hasChildren && isOpen ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.2s ease"
            }}
          />
        </IconButton>
      ) : (
        <Stack sx={{ padding: isEmptyDataRoomRootFolder ? "0px" : "16px" }} />
      )}
      <Stack
        direction="row"
        alignItems="center"
        gap="5px"
        onClick={handleTitleClick}
        sx={{
          flex: 1,
          minWidth: 0,
          cursor: "pointer"
        }}
      >
        {folderIcon}
        <MaterialTooltip title={folder.title}>
          <Typography
            noWrap
            sx={{
              color: labelColor,
              fontWeight: 400,
              fontSize: "16px"
            }}
          >
            {folder.title}
          </Typography>
        </MaterialTooltip>
        {showHint && (
          <MaterialTooltip title={showHint} isShow>
            <InfoOutlinedIcon sx={{ fontSize: 15, color: "#0A3142", opacity: 0.7 }} />
          </MaterialTooltip>
        )}
      </Stack>
      <Stack direction="row" alignItems="center" sx={{ minWidth: folder.is_private ? 53 : 32 }}>
        {folder.is_private && <VisibilityLock sx={{ fill: "#BAC7CE" }} />}

        <SharedDealGuard>
          <DealRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={isAnalystDealsViewerPermission}>
            <StatusComponentGuard statuses={[opportunityId]} show={false}>
              <FolderActions folder={folder} refetchHandler={refetchFolderMetadata} />
            </StatusComponentGuard>
          </DealRoleGuard>
        </SharedDealGuard>
      </Stack>
    </Stack>
  );
});
